import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const LoginPage = () => {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(formData.email, formData.password);
      toast.success(t('common_success'));
      
      if (user.user_type === 'professional') {
        navigate('/dashboard');
      } else {
        // Free model: all users (azienda_manifatturiera, azienda_automazione, end_user) → directory
        navigate('/directory');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="login-page">
      <Card className="w-full max-w-md border border-slate-200 rounded-md shadow-sm">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-slate-900 rounded-sm flex items-center justify-center">
              <span className="text-orange-500 font-bold text-xl">A</span>
            </div>
          </div>
          <CardTitle 
            className="text-2xl font-semibold text-slate-900"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {t('auth_login_title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                {t('auth_email')}
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="rounded-sm"
                data-testid="login-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                {t('auth_password')}
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="rounded-sm"
                data-testid="login-password"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {t('auth_login_btn')}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm text-slate-600">
            <span>{t('auth_no_account')} </span>
            <Link 
              to="/register" 
              className="text-orange-500 hover:text-orange-600 font-medium"
              data-testid="login-register-link"
            >
              {t('nav_register')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
